var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { useLocalization, useInternationalization } from '@progress/kendo-react-intl';
import { messages, recurrenceEditorRepeat, recurrenceEditorEndLabel, recurrenceEditorEndAfter, recurrenceEditorEndNever, recurrenceEditorEndOccurrence, recurrenceEditorEndOn, recurrenceEditorWeeklyRepeatOn, recurrenceEditorYearlyRepeatOn, recurrenceEditorMonthlyRepeatOn, recurrenceEditorMonthlyDay, recurrenceEditorYearlyOf } from '../../messages';
import { isPresent, capitalize } from '../../utils';
import { serializeRule } from '@progress/kendo-recurrence';
import { ZonedDate } from '@progress/kendo-date-math';
import { RecurrenceFrequencyEditor } from './RecurrenceFrequencyEditor';
import { RecurrenceIntervalEditor } from './RecurrenceIntervalEditor';
import { RecurrenceEndEditor } from './RecurrenceEndEditor';
import { RecurrenceRepeatOnWeekEditor } from './RecurrenceRepeatOnWeekEditor';
import { RecurrenceRepeatOnMonthYearEditor } from './RecurrenceRepeatOnMonthYearEditor';
import { parseRRule, getFrequencies, getFrequency, getUntil, getMonths, getWeekDays, getExtendedWeekDays, getOffsetPositions, getEndRule, getMonthDay, getRepeatOnRule, weekDayRuleFromString } from './common';
/** @hidden */
export var SchedulerRecurrenceEditor = function (props) {
    var start = props.start, timezone = props.timezone, onFocus = props.onFocus, onBlur = props.onBlur, onChange = props.onChange;
    var intlService = useInternationalization();
    var localizationService = useLocalization();
    var localizeMessage = function (baseMassage, removePrepend) {
        var message = removePrepend ? baseMassage : 'scheduler.recurrenceEditor' + baseMassage;
        return localizationService.toLanguageString(message, messages[message]);
    };
    var rrule = React.useMemo(function () { return parseRRule(props.value); }, [props.value]);
    var frequencies = React.useMemo(function () { return getFrequencies(localizeMessage); }, []);
    var frequency = React.useMemo(function () { return getFrequency(rrule); }, [rrule]);
    var until = React.useMemo(function () { return getUntil(rrule); }, [rrule.until]);
    var months = React.useMemo(function () { return getMonths(intlService); }, []);
    var weekDays = React.useMemo(function () { return getWeekDays(intlService); }, []);
    var extendedWeekDays = React.useMemo(function () { return getExtendedWeekDays(intlService, localizeMessage); }, []);
    var offsetPositions = React.useMemo(function () { return getOffsetPositions(localizeMessage); }, []);
    var endRule = getEndRule(rrule);
    var monthDay = getMonthDay(rrule, start);
    var repeatOnRule = React.useMemo(function () { return getRepeatOnRule(rrule); }, [rrule.byWeekDay, rrule.byMonthDay]);
    var onChangeHandler = React.useCallback(function (newRrule) {
        if (newRrule.freq === 'never') {
            onChange({ value: null });
        }
        else {
            if (newRrule.weekStart === undefined) {
                newRrule.weekStart = intlService.firstDay();
            }
            onChange({ value: serializeRule(newRrule, timezone) });
        }
    }, [onChange, timezone]);
    var onFrequencyChange = React.useCallback(function (newFreq) {
        var newRrule = {};
        newRrule.freq = newFreq;
        newRrule.interval = 1;
        if (newFreq === 'weekly') {
            newRrule.byWeekDay = [{
                    day: start.getDay(),
                    offset: 0
                }];
        }
        if (newFreq === 'monthly' || newFreq === 'yearly') {
            newRrule.byMonthDay = [start.getDate()];
        }
        if (newFreq === 'yearly') {
            newRrule.byMonth = [start.getMonth() + 1];
        }
        onChangeHandler(newRrule);
    }, [onChangeHandler, start]);
    var onIntervalChange = React.useCallback(function (e) {
        onChangeHandler(Object.assign({}, rrule, {
            interval: e.value
        }));
    }, [onChangeHandler, rrule]);
    var onCountChange = React.useCallback(function (newCount) {
        onChangeHandler(Object.assign({}, rrule, {
            count: newCount
        }));
    }, [onChangeHandler, rrule]);
    var onUntilChange = React.useCallback(function (newUntil) {
        onChangeHandler(Object.assign({}, rrule, {
            until: ZonedDate.fromLocalDate(newUntil, timezone)
        }));
    }, [onChangeHandler, rrule, timezone]);
    var onWeekDaysChange = React.useCallback(function (dayIndex, selected) {
        var isAdd = selected === 'false';
        var parsedDayIndex = parseInt(dayIndex, 10);
        var newWeekDays = (rrule.byWeekDay || []).map(function (day) { return (__assign({}, day)); });
        if (isAdd) {
            newWeekDays.push({
                offset: 0,
                day: parsedDayIndex
            });
        }
        else {
            var indexToRemove = newWeekDays.findIndex(function (weekDay) { return weekDay.day === parsedDayIndex; });
            if (indexToRemove > -1) {
                newWeekDays.splice(indexToRemove, 1);
            }
        }
        onChangeHandler(Object.assign({}, rrule, {
            byWeekDay: newWeekDays
        }));
    }, [onChangeHandler, rrule]);
    var onWeekDayChange = React.useCallback(function (weekDay, offset) {
        var newRrule = Object.assign({}, rrule);
        if (typeof weekDay === 'string') {
            /* day, weekday or weekend */
            newRrule.byWeekDay = weekDayRuleFromString(weekDay);
            newRrule.bySetPosition = [offset];
        }
        else {
            /* specific weekday */
            newRrule.byWeekDay = [{
                    day: weekDay,
                    offset: offset
                }];
        }
        onChangeHandler(newRrule);
    }, [onChangeHandler, rrule]);
    var onMonthDaysChange = React.useCallback(function (newMonthDays) {
        onChangeHandler(Object.assign({}, rrule, {
            byMonthDay: newMonthDays
        }));
    }, [onChangeHandler, rrule]);
    var onMonthsChange = React.useCallback(function (newMonths) {
        onChangeHandler(Object.assign({}, rrule, {
            byMonth: newMonths
        }));
    }, [onChangeHandler, rrule]);
    var onEndRuleChange = React.useCallback(function (event) {
        var newRrule = Object.assign({}, rrule);
        if (event.value === 'count') {
            newRrule.until = undefined;
            newRrule.count = event.count;
        }
        else if (event.value === 'until') {
            newRrule.until = ZonedDate.fromLocalDate(event.until, timezone);
            newRrule.count = undefined;
        }
        else {
            /* never */
            newRrule.until = undefined;
            newRrule.count = undefined;
        }
        onChangeHandler(newRrule);
    }, [onChangeHandler, rrule, timezone]);
    var onRepeatOnRuleChange = React.useCallback(function (newRepeatOnRule, weekDay, offset) {
        var newRrule = Object.assign({}, rrule);
        if (newRepeatOnRule === 'monthday') {
            newRrule.byWeekDay = undefined;
            newRrule.bySetPosition = undefined;
            newRrule.byMonthDay = [monthDay];
        }
        else if (newRepeatOnRule === 'weekday') {
            // weekDays
            newRrule.byMonthDay = undefined;
            if (typeof weekDay === 'string') {
                /* day, weekday or weekend */
                newRrule.bySetPosition = [offset];
                newRrule.byWeekDay = weekDayRuleFromString(weekDay);
            }
            else {
                /* specific weekday */
                newRrule.byWeekDay = [{
                        day: weekDay,
                        offset: offset
                    }];
            }
        }
        onChangeHandler(newRrule);
    }, [onChangeHandler, rrule, monthDay]);
    var onOffsetPositionChange = React.useCallback(function (offset) {
        var newRrule = Object.assign({}, rrule);
        if (isPresent(newRrule.byWeekDay)) {
            var weekDaysCount = newRrule.byWeekDay.length;
            switch (weekDaysCount) {
                case 7:
                case 5:
                case 2:
                    newRrule.bySetPosition = [offset];
                    break;
                case 1:
                    newRrule.byWeekDay[0].offset = offset;
                    break;
                default:
                    break;
            }
        }
        onChangeHandler(newRrule);
    }, [onChangeHandler, rrule]);
    var onFocusHandler = React.useCallback(function () {
        onFocus();
    }, [onFocus]);
    var onBlurHandler = React.useCallback(function () {
        onBlur();
    }, [onBlur]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { onFocus: onFocusHandler, onBlur: onBlurHandler },
            React.createElement(RecurrenceFrequencyEditor, { titleClassName: 'k-edit-label', editorClassName: 'k-edit-field', title: localizeMessage(recurrenceEditorRepeat, true), frequency: frequency, frequencies: frequencies, onFrequencyChange: onFrequencyChange })),
        frequency !== 'never' &&
            (React.createElement("div", { onFocus: onFocusHandler, onBlur: onBlurHandler },
                React.createElement(RecurrenceIntervalEditor, { titleClassName: 'k-edit-label', editorClassName: 'k-edit-field', title: localizeMessage(capitalize(frequency) + 'RepeatEvery'), frequencyText: localizeMessage(capitalize(frequency) + 'Interval'), interval: rrule.interval, onIntervalChange: onIntervalChange }))),
        frequency === 'weekly' &&
            (React.createElement("div", { onFocus: onFocusHandler, onBlur: onBlurHandler },
                React.createElement(RecurrenceRepeatOnWeekEditor, { titleClassName: 'k-edit-label', editorClassName: 'k-edit-field', title: localizeMessage(recurrenceEditorWeeklyRepeatOn, true), weekDays: weekDays, byWeekDay: rrule.byWeekDay, onWeekDaysChange: onWeekDaysChange }))),
        (frequency === 'monthly' || frequency === 'yearly') &&
            (React.createElement("div", { onFocus: onFocusHandler, onBlur: onBlurHandler },
                React.createElement(RecurrenceRepeatOnMonthYearEditor, { titleClassName: 'k-edit-label', editorClassName: 'k-edit-field', titleMonthly: localizeMessage(recurrenceEditorMonthlyRepeatOn, true), titleYearly: localizeMessage(recurrenceEditorYearlyRepeatOn, true), monthlyDayTitle: localizeMessage(recurrenceEditorMonthlyDay, true), yearlyOf: localizeMessage(recurrenceEditorYearlyOf, true), monthDay: monthDay, frequency: frequency, repeatOnRule: repeatOnRule, start: start, rrule: rrule, months: months, extendedWeekDays: extendedWeekDays, offsetPositions: offsetPositions, onRepeatOnRuleChange: onRepeatOnRuleChange, onMonthDaysChange: onMonthDaysChange, onMonthsChange: onMonthsChange, onOffsetPositionChange: onOffsetPositionChange, onWeekDayChange: onWeekDayChange }))),
        frequency !== 'never' &&
            (React.createElement("div", { onFocus: onFocusHandler, onBlur: onBlurHandler },
                React.createElement(RecurrenceEndEditor, { title: localizeMessage(recurrenceEditorEndLabel, true), endAfter: localizeMessage(recurrenceEditorEndAfter, true), endNever: localizeMessage(recurrenceEditorEndNever, true), endOccurrence: localizeMessage(recurrenceEditorEndOccurrence, true), endOn: localizeMessage(recurrenceEditorEndOn, true), titleClassName: 'k-edit-label', editorClassName: 'k-edit-field', count: rrule.count, endRule: endRule, start: start, until: until, timezone: timezone, onCountChange: onCountChange, onUntilChange: onUntilChange, onEndRuleChange: onEndRuleChange })))));
};
SchedulerRecurrenceEditor.defaultProps = {
    value: ''
};
SchedulerRecurrenceEditor.displayName = 'KendoReactSchedulerRepeatEditor';
//# sourceMappingURL=SchedulerRecurrenceEditor.js.map